import { Box, Paper, Typography, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useGetPhoneNumbers } from 'apis/business/agent/deployPhone/hook';
import Grid from '@mui/material/Grid2';
import { useGetAgent } from 'apis/business/agent/hook';
import Spinner from 'components/Common/Spinner';
import { MdOutlineLocalPhone } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';
import BuyNewNumberForm from './components/BuyNewNumberForm';
import ImportNumberForm from './components/ImportNumberForm';

export default function DeployPhone() {
  const theme = useTheme();
  const { data: agent, isLoading } = useGetAgent();
  const [showNumber, setShowNumber] = useState<'existing' | boolean | null>(null);
  const [numberType] = useState<'existing' | 'new' | null>(null);
  const [selectedOption, setSelectedOption] = useState<'buy' | 'import' | null>(
    agent?.active_number?.imported === false ? 'buy' : agent?.active_number?.imported === true ? 'import' : null
  );

  useEffect(() => {
    setSelectedOption(
      agent?.active_number?.imported === false ? 'buy' : agent?.active_number?.imported === true ? 'import' : null
    );
  }, [agent]);

  const { watch } = useForm({
    values: {
      phone_number: {
        country_code: 'CA',
        phone_number: agent?.phone_number
      },
      agent: {
        availability: agent?.availability || ''
      }
    }
  });

  const { data: phoneNumbers } = useGetPhoneNumbers(
    watch('phone_number.country_code'),
    String(watch('phone_number.phone_number')),
    {
      queryKey: ['phoneNumbers', watch('phone_number.country_code'), numberType],
      enabled: Boolean(showNumber && numberType)
    },
    numberType!,
    showNumber
  );

  useEffect(() => {
    setShowNumber(false);
  }, [phoneNumbers]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box>
      {selectedOption === null && (
        <Box bgcolor={theme.palette.background.paper} padding={3} borderRadius={1}>
          <Typography variant='h6' marginBottom={1} fontSize='500'>
            Deploy Phone
          </Typography>
          <Typography variant='body2'>Choose how you want to deploy your agent to a phone number</Typography>
          <Grid container spacing={3} mt={3}>
            {options.map((option) => (
              <Grid size={{ xs: 12, md: 6 }} key={option.key}>
                <Paper
                  elevation={selectedOption === option.key ? 6 : 1}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: selectedOption === option.key ? '2px solid #7c3aed' : '1px solid #e0e0e0',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: selectedOption === option.key ? '#f3f0ff' : 'white',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                  onClick={() => setSelectedOption(option.key as 'buy' | 'import')}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.light,
                      margin: '0 auto 16px'
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography variant='h6' fontWeight='bold' mb={1}>
                    {option.title}
                  </Typography>
                  <Typography color='text.secondary' mb={2}>
                    {option.description}
                  </Typography>
                  <Button variant='outlined' endIcon={<span>&rarr;</span>}>
                    Get Started
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {selectedOption === 'buy' && <BuyNewNumberForm setSelectedOption={setSelectedOption} />}
      {selectedOption === 'import' && <ImportNumberForm setSelectedOption={setSelectedOption} />}
    </Box>
  );
}

const options = [
  {
    key: 'buy',
    title: 'Buy New Number',
    description: 'Purchase a new phone number for your business agent',
    icon: <MdOutlineLocalPhone color='primary' />
  },
  {
    key: 'import',
    title: 'Import Phone Number',
    description: 'Use your existing phone number with the agent',
    icon: <FaPlus color='primary' />
  }
];
